import Http from "./Http";

export class UsersService extends Http {
  /**
   * Register user via email, username and password
   * @param {Object} body
   * @param {string} body.email
   * @param {string} body.password
   * @param {string} body.username
   * @param {string} body.referral
   * @param {boolean} body.isAdultConfirmation
   * @param {boolean} body.isAcceptedTermsAndPrivacy
   * @param {string} body.trackingCode
   */
  register(body) {
    return this.post("/users", body);
  }

  /**
   * Get list of creators
   * @param {Object} params
   * @param {integer} params.limit
   * @param {integer} params.offset
   * @param {string} params.q
   */
  getCreators(params) {
    return this.get("/users", { params });
  }

  /**
   * Get current user profile
   */
  getMyProfile() {
    return this.get("/users/me");
  }

  /**
   * Update current user profile
   * @param {Object} body
   * @param {string|null} body.username
   * @param {string|null} body.name
   * @param {string|null} body.bio yyyy-mm-dd format, e.g. `2004-03-22`
   * @param {string|null} body.dob
   * @param {Array.<{type: integer, link: string}>} body.socialLinks
   */
  updateMyProfile(body) {
    return this.put("/users/me", body);
  }

  /**
   * Set image as avatar
   * @param {Object} body
   * @param {number} body.fileId
   */
  uploadAvatar(body) {
    return this.put("/users/avatar", body);
  }

  /**
   * Delete current user avatar
   */
  deleteAvatar() {
    return this.delete("/users/avatar");
  }

  /**
   * Set image as avatar
   * @param {Object} body
   * @param {number} body.fileId
   */
  uploadBanner(body) {
    return this.put("/users/banner", body);
  }

  /**
   * Delete current user avatar
   */
  deleteBanner() {
    return this.delete("/users/banner");
  }

  /**
   * Update current user email
   * @param {Object} body
   * @param {string} body.currentEmail
   * @param {string} body.newEmail
   * @param {string} body.twoFactorCode
   */
  updateEmail(body) {
    return this.put("/users/email", body);
  }

  /**
   * Change user password
   * @param {Object} body
   * @param {string} body.password
   * @param {string} body.newPassword
   * @param {string} body.twoFactorCode
   */
  updatePassword(body) {
    return this.patch("/users/password", body);
  }

  /**
   * Get user profile by username
   * @param {string} username
   */
  getUserByUsername(username) {
    return this.get(`/users/${username}`, {
      headers: {
        "cache-control": "no-cache",
      },
    });
  }

  /**
   * Report creator by id
   * @param {number} id
   * @param {object} body
   * @param {number} body.type report type. 1 - spam, 2 - fake, 3 - violence, 5 - copyright, 6 - other
   */
  reportCreatorById(id, body) {
    return this.post(`/users/${id}/reports`, body);
  }

  /**
   * Don't show users reports notification again
   * @param {number} id
   */
  dontShowReportNotification(id) {
    return this.patch(`/users/${id}/reports/notifications`);
  }

  /**
   * Block user by id, so he cant access my account
   * @param {object} body
   * @param {number} body.userId
   * @returns
   */
  blockUserByUserId(body) {
    return this.post("/users/me/blocklists", body);
  }

  /**
   * Unblock user by id, so he can access my account
   * @param {object} body
   * @param {number} body.userId
   * @returns
   */
  unblockUserByUserId(body) {
    return this.delete("/users/me/blocklists", { data: body });
  }

  /**
   * Get list of my followers
   * @param {object} params
   * @param {number} params.limit
   * @param {offset} params.offset
   * @returns
   */
  getMyFollowers(params) {
    return this.get("/users/me/followers", { params });
  }

  /**
   * Get list of my followers
   * @param {object} params
   * @param {number} params.limit
   * @param {offset} params.offset
   * @returns
   */
  getMyFollowings(params) {
    return this.get("/users/me/followings", { params });
  }

  /**
   * Get list of my supporters
   * @param {object} params
   * @param {number} params.limit
   * @param {offset} params.offset
   * @returns
   */
  getMySupporters(params) {
    return this.get("/users/me/supporters", { params });
  }

  /**
   * Get list of my promoters
   * @param {object} params
   * @param {number} params.limit
   * @param {offset} params.offset
   * @returns
   */
  getMyPromoters(params) {
    return this.get("/users/me/promoters", { params });
  }

  /**
   * Follow creator by id
   * @param {number} id
   * @param {object} body
   * @param {string?=} body.trackingCode
   * @returns
   */
  followCreator(id, body) {
    return this.post(`/users/${id}/followers`, body);
  }

  /**
   * Get user's list of followers by user id
   * @param {number} id
   * @param {object} params
   * @param {number} params.limit
   * @param {offset} params.offset
   * @returns
   */
  followersByUserId(id, params) {
    return this.get(`/users/${id}/followers`, { params });
  }

  /**
   * Get user's list of followings  by user id
   * @param {number} id
   * @param {object} params
   * @param {number} params.limit
   * @param {offset} params.offset
   * @returns
   */
  followingsByUserId(id, params) {
    return this.get(`/users/${id}/followings`, { params });
  }

  /**
   * Get user's list of supporters by user id
   * @param {number} id
   * @param {object} params
   * @param {number} params.limit
   * @param {offset} params.offset
   * @returns
   */
  supportersByUserId(id, params) {
    return this.get(`/users/${id}/supporters`, { params });
  }
  /**
   * Get suggestions list of creators
   * @param {object} params
   * @param {number} params.limit
   * @param {offset} params.offset
   * @returns
   */
  getSuggestions(params) {
    return this.get("/users/suggestions", { params });
  }

  /**
   * Get user's list of promoters by user id
   * @param {number} id
   * @param {object} params
   * @param {number} params.limit
   * @param {offset} params.offset
   * @returns
   */
  promotersByUserId(id, params) {
    return this.get(`/users/${id}/promoters`, { params });
  }

  /**
   * Unfollow creator by id
   * @param {number} id
   * @returns
   */
  unfollowCreator(id) {
    return this.delete(`/users/${id}/followers`);
  }

  /**
   * Send donation to user via Stripe
   * @param {number} id
   * @param {object} body
   * @param {number} body.amount in cents or MATIC
   * @param {string} body?.message
   * @param {number} body.cardId
   * @param {number} [body.postId]
   * @param {string} [body.referral]
   * @returns
   */
  sendDonationToUserById(id, body) {
    return this.post(`/users/${id}/donations`, body);
  }

  /**
   * Get  current user block-list
   * @param {object} params
   * @param {number} params.limit
   * @param {offset} params.offset
   * @returns
   */
  getUserBlocklist(params) {
    return this.get(`/users/me/blocklists`, { params });
  }

  /**
   * Create conversation with user
   * @param {number} id opponent id
   * @returns
   */
  createConversation(id) {
    return this.post(`/users/${id}/conversations`);
  }

  /**
   * Delete current user profile
   * @param {object} body
   * @param {string} body.reason
   */
  deleteMyProfile(body) {
    return this.delete("/users/me", { data: body });
  }

  /**
   * Set flag that share content tour was shown
   */
  updateContentShareTourModel() {
    return this.patch("users/is-contentShareTourModelClosed");
  }

  /**
   * Update isPushNotificationsAllowed flag
   * @param {object} body
   * @param {boolean} body.state
   * @returns
   */
  updatePushesState(body) {
    return this.patch(`/users/push-notifications/is-allowed`, body);
  }

  /**
   * Add following to favorites
   * @param {number} id
   * @returns
   */
  addFollowingToFavorite(id) {
    return this.post(`/users/me/followings/${id}/favorite`);
  }

  /**
   * Delete following from favorites by followingId
   * @param {number} id
   * @returns
   */
  removerFollowingFromFavorite(id) {
    return this.delete(`/users/me/followings/${id}/favorite`);
  }

  /**
   * Update isProfileTourCompleted flag
   * @param {object} body
   * @param {boolean} body.state
   * @returns
   */
  updateUserProfileTour(body) {
    return this.patch("users/profile-tour/completed", body);
  }
}

export default new UsersService();
